import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { BusinessCard } from "./BusinessCard.jsx";
import { ClientSwitcher } from "./ClientSwitcher.jsx";

/**
 * 220px fixed-width sidebar. Groups pages by `group` field and renders each
 * group under a small uppercase section label.
 *
 * Active item styling (per CLAUDE.md):
 *   - accent-tinted background
 *   - 2px accent bar on the right edge
 *   - font-weight 600
 *
 * Inactive items use textSecondary color and hover to a subtle gray.
 *
 * Props:
 *   pages       : [{ key, label, group }]   — from @prism/shared/modules
 *   activePage  : string                    — the key of the current page
 *   onNavigate  : (pageKey) => void
 *   client      : ClientShape               — passed through to BusinessCard
 */
const GROUP_LABELS = {
  main: "Dashboard",
  admin: "Admin",
  account: "Settings",
};

export function Sidebar({ pages, activePage, onNavigate, client, syncStatus }) {
  const grouped = pages.reduce((acc, p) => {
    const g = p.group || "main";
    (acc[g] ||= []).push(p);
    return acc;
  }, {});

  // Render groups in a stable order: main first, then anything else, account last.
  const groupOrder = ["main", ...Object.keys(grouped).filter((g) => g !== "main" && g !== "account"), "account"];

  return (
    <aside className="w-[220px] flex-shrink-0 bg-surface border-r border-border-default flex flex-col">
      {/* Brand */}
      <div className="px-5 py-5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-accent flex items-center justify-center text-white text-[11px] font-bold">
            P
          </div>
          <span className="text-[14px] font-semibold text-text-primary tracking-tight">
            Prism
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto">
        {groupOrder.map((group) => {
          const items = grouped[group];
          if (!items?.length) return null;
          return (
            <div key={group} className="mb-5">
              <div
                className="px-5 mb-1.5 text-[10px] uppercase text-text-muted font-medium"
                style={{ letterSpacing: "0.5px" }}
              >
                {GROUP_LABELS[group] || group}
              </div>
              <ul>
                {items.map((page) => (
                  <NavItem
                    key={page.key}
                    page={page}
                    active={page.key === activePage}
                    onClick={() => onNavigate?.(page.key)}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <ClientSwitcher />
      {client && <BusinessCard client={client} syncStatus={syncStatus} />}
      <SignOut />
    </aside>
  );
}

/**
 * Deliberate sign-out at the bottom of the sidebar (the Profile page keeps
 * its button too). Same pattern as Profile.jsx: clear the session AND land
 * on a clean /login, so ProtectedRoute never stashes a `from` location and
 * the next sign-in goes through the role-aware HomeRedirect (the PR #4 fix).
 * Session-expiry logout via the 401 path still resumes the interrupted page.
 */
function SignOut() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  function handleSignOut() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="border-t border-border-light px-4 py-3">
      <button
        type="button"
        onClick={handleSignOut}
        className="text-[12px] text-text-secondary hover:text-text-primary transition-colors"
      >
        Sign out
      </button>
    </div>
  );
}

function NavItem({ page, active, onClick }) {
  const base =
    "w-full text-left pl-5 pr-4 py-2 text-[13px] flex items-center justify-between relative transition-colors";
  const state = active
    ? "bg-accent-bg font-semibold text-text-primary"
    : "font-normal text-text-secondary hover:bg-[var(--color-border-light)]";
  return (
    <li>
      <button type="button" onClick={onClick} className={`${base} ${state}`}>
        <span className="truncate">{page.label}</span>
        {active && (
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 bottom-0 w-0.5 bg-accent"
          />
        )}
      </button>
    </li>
  );
}
